import { DataSourceField } from "abweb/dto/ds/DataSourceField";
import { DataSourceValue } from "abweb/dto/ds/DataSourceValue";
import { DataFieldTypeEnum } from "abweb/dto/generated/ds/DataFieldTypeEnum";
import { DataSourceState } from "abweb/dto/generated/ds/DataSourceState";
import { DataSourceValueDateBase } from "abweb/dto/generated/ds/DataSourceValueDateBase";
import * as AbWebResources from "abweb/generated/locale/AbWebResources";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { DateTimeUtils } from "abweb/utils/date/DateTimeUtils";
import { DateUtils } from "abweb/utils/DateUtils";
/**
 * DataSourceValueDate represents a date at midnight on a particular day (i.e. a YYYY/MM/DD value with no time value).
 * It is designed to serialize to and from UTC+0 values involving server (json) data, but store local values for use
 * in the client. All operations that get/set Date values from the client should be setting local values at midnight.
 * Any attempt to set a Date value that is not at midnight local time will throw an exception.
 */
export class DataSourceValueDate extends DataSourceValueDateBase {
  static createWithReadOnlyDefaults(id, value, refID = null) {
    return DataSourceValueDate.createDataSourceValueDate(/*attributeValues*/null, /*clientTag*/null, /*dataSourceSaveAction*/null, id, /*baselineRefID*/null, refID, DataSourceState.NORMAL, /*baseline*/null, value, /*editableOverwrite*/null, /*fieldTypeOverwrite*/null);
  }
  static createWithFactoryDefaults(value, refID = null) {
    const dateValue = DataSourceValueDate.createDataSourceValueDate(/*attributeValues*/null, /*clientTag*/null, /*dataSourceSaveAction*/null, /*id*/null, /*baselineRefID*/null, refID, DataSourceState.NORMAL, /*baseline*/null, value, /*editableOverwrite*/null, /*fieldTypeOverwrite*/null);
    dateValue._field = DataSourceField.createWithDefaults(DataFieldTypeEnum.DATE, "Name");
    return dateValue;
  }
  static createDataSourceValueDate(attributeValues, clientTag, dataSourceSaveAction, id, baselineRefID, refID, state, baseline, value, editableOverwrite = null, fieldTypeOverwrite = null) {
    const retVal = new DataSourceValueDate();
    retVal.initDataSourceValueDate(attributeValues, clientTag, dataSourceSaveAction, editableOverwrite, id, state, baselineRefID, fieldTypeOverwrite, refID, null, null, null);
    // Assign Date values after the initialization, as that function would otherwise assume the Dates were UTC and do
    // a conversion on them.
    retVal.baseline = baseline;
    retVal.value = value;
    retVal.currentValue = value;
    return retVal;
  }
  constructor() {
    super();
  }

  // This is called when coming FROM tsog, where Date values coming in are expected to be in UTC-midnight format.
  initDataSourceValueDate(attributeValues, clientTag, dataSourceSaveAction, editableOverwrite, id, state, baselineRefID, fieldTypeOverwrite, refID, baseline, currentValue, value) {
    // If the server is sending a DataSourceValueDate, it is sending the Date values as midnight UTC. This converts to
    // midnight local time so that the client can safely work against local times.
    baseline = DateUtils.convertDateFromUtcToLocal(baseline);
    currentValue = DateUtils.convertDateFromUtcToLocal(currentValue);
    value = DateUtils.convertDateFromUtcToLocal(value);
    super.initDataSourceValueDate(attributeValues, clientTag, dataSourceSaveAction, editableOverwrite, id, state, baselineRefID, fieldTypeOverwrite, refID, baseline, currentValue, value);
    if (currentValue == null) {
      this.currentValue = value;
    }
  }

  /**
   * This is an override of the generated toTSOG method that converts the local Date values to UTC-midnight values.
   * For DATE values, we truncate to midnight. For DATETIME values, we leave the values alone.
   */
  toTSOG(result, serializeContext) {
    const retVal = super.toTSOG(result, serializeContext);
    if (this.fieldType === DataFieldTypeEnum.DATE) {
      if (this.baseline != null) {
        retVal["baseline"] = DateUtils.convertDateFromLocalToUtc(this.baseline);
      }
      if (this.currentValue != null) {
        retVal["currentValue"] = DateUtils.convertDateFromLocalToUtc(this.currentValue);
      }
      if (this.value != null) {
        retVal["value"] = DateUtils.convertDateFromLocalToUtc(this.value);
      }
    }
    return retVal;
  }
  getCurrentValue() {
    return this.currentValue;
  }
  getValue() {
    return this.value;
  }
  getBaseline() {
    return this.baseline;
  }
  get isDate() {
    if (this._field) {
      return this._field.fieldType === DataFieldTypeEnum.DATE;
    } else {
      return true;
    }
  }
  get isDateTime() {
    if (this._field) {
      return this._field.fieldType === DataFieldTypeEnum.DATETIME;
    } else {
      return false;
    }
  }
  getDisplayValue() {
    const date = this.get("currentValue");
    return this.getFormattedValue(date);
  }
  getFormattedValue(value) {
    return value ? this.isDateTime ? DateTimeUtils.DATETIME_WITH_SECONDS.format(value) : DateTimeUtils.DATE_SHORT.format(value) : "";
  }
  validateValue(value) {
    const date = value;
    const error = super.validateValue(date);
    if (error != null) {
      return error;
    }
    if (this._field == null || this._field.validation == null) {
      // Nothing to validate
      return null;
    }
    const validation = this._field.validation;
    if (validation.dateRangeMin != null) {
      if (date == null || DateUtils.compare(date, validation.dateRangeMin) < 0) {
        return AbWebResources.rangeMinValidation(DateTimeUtils.DATE_SHORT.format(validation.dateRangeMin));
      }
    }
    if (validation.dateRangeMax != null) {
      if (date == null || DateUtils.compare(date, validation.dateRangeMax) > 0) {
        return AbWebResources.rangeMaxValidation(DateTimeUtils.DATE_SHORT.format(validation.dateRangeMax));
      }
    }
    return null;
  }

  /**
   * Tests for equality
   * @param value1 Assumed to be a Date object.
   * @param value2 Assumed to be a Date object.
   * @returns {boolean}
   */
  areEqual(value1, value2) {
    return DateUtils.areEqual(value1, value2);
  }
  coerceValue(value) {
    if (value == null) {
      return null;
    }
    if (ClassUtils.isInstanceOf(value, DataSourceValue)) {
      if (value.isReference) {
        throw new Error("Cannot coerce a REFERENCE value to DATE");
      }
      value = value.getCurrentValue();
    }
    if (value == null) {
      return null;
    }
    if (ClassUtils.isInstanceOf(value, Date)) {
      return DateUtils.clone(value);
    }
    const t = typeof value;
    if (t === "string") {
      if (value === "") {
        return null;
      }
      const date = DateTimeUtils.kendoParseDate(value);
      if (date == null) {
        throw new Error(`Cannot coerce '${value.toString()}' to DATE`);
      }
      return date;
    }
    throw new Error(`Cannot coerce ${value.toString()} to DATE`);
  }
  initializeWithDefaultFieldValue(field) {
    if (field.defaultValue == null) {
      return;
    }
    const defaultValue = this.coerceValue(field.defaultValue);
    this.value = this.currentValue = defaultValue;
  }

  /**
   * Compare function that can be used for sorting.
   * @param bvalue Value to compare against.
   * @return negative number if this value is less than bvalue,
   * positive number if bvalue is less than this value, 0 if equal.
   */
  compare(bvalue) {
    let bd = bvalue;
    let a = this.currentValue;
    let b = bd == null ? null : bd.currentValue;
    return DateUtils.compare(a, b);
  }
}
//# sourceMappingURL=DataSourceValueDate.js.map
